import React from 'react';
import { BaseModal } from './BaseModal';
import { getButtonClasses, ButtonVariant } from './ModalVariants';

/**
 * Confirm Modal Component
 * Specialized modal for confirmation dialogs with customizable variants
 */

// =============================================================================
// Confirm Modal Props Interface
// =============================================================================

export interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info' | 'success';
  loading?: boolean;
  loadingText?: string;
  icon?: React.ReactNode;
  showIcon?: boolean;
}

// =============================================================================
// Variant Configuration for Confirm Modal
// =============================================================================

interface ConfirmVariantConfig {
  modalVariant: 'danger' | 'warning' | 'info' | 'success';
  confirmButtonVariant: ButtonVariant;
  titleClass: string;
  iconClass: string;
  messageClass: string;
}

const confirmVariantConfigs: Record<string, ConfirmVariantConfig> = {
  danger: {
    modalVariant: 'danger',
    confirmButtonVariant: 'danger',
    titleClass: 'text-red-900',
    iconClass: 'text-red-500',
    messageClass: 'text-red-800',
  },
  warning: {
    modalVariant: 'warning',
    confirmButtonVariant: 'warning',
    titleClass: 'text-amber-900',
    iconClass: 'text-amber-500',
    messageClass: 'text-amber-800',
  },
  info: {
    modalVariant: 'info',
    confirmButtonVariant: 'info',
    titleClass: 'text-sky-900',
    iconClass: 'text-sky-500',
    messageClass: 'text-sky-800',
  },
  success: {
    modalVariant: 'success',
    confirmButtonVariant: 'success',
    titleClass: 'text-emerald-900',
    iconClass: 'text-emerald-500',
    messageClass: 'text-emerald-800',
  },
};

// =============================================================================
// Default Icons for Each Variant
// =============================================================================

const getDefaultIcon = (variant: string) => {
  switch (variant) {
    case 'danger':
      return (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.856-.833-2.626 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
          />
        </svg>
      );
    case 'warning':
      return (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.856-.833-2.626 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
          />
        </svg>
      );
    case 'info':
      return (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      );
    case 'success':
      return (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      );
    default:
      return null;
  }
};

// =============================================================================
// Confirm Modal Component
// =============================================================================

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Emin misiniz?',
  message = 'Bu işlemi gerçekleştirmek istediğinizden emin misiniz?',
  confirmText = 'Evet',
  cancelText = 'İptal',
  variant = 'warning',
  loading = false,
  loadingText = 'İşleniyor...',
  icon,
  showIcon = true,
}) => {
  const config = confirmVariantConfigs[variant] || confirmVariantConfigs.warning;
  const displayIcon = icon || (showIcon ? getDefaultIcon(variant) : null);

  const handleConfirm = async () => {
    if (!loading) {
      onConfirm();
    }
  };

  const handleCancel = () => {
    if (!loading) {
      onClose();
    }
  };

  const footer = (
    <div className="flex gap-3 justify-end">
      <button
        type="button"
        onClick={handleCancel}
        disabled={loading}
        className={getButtonClasses('secondary')}
      >
        {cancelText}
      </button>
      <button
        type="button"
        onClick={handleConfirm}
        disabled={loading}
        className={getButtonClasses(config.confirmButtonVariant)}
      >
        {loading ? loadingText : confirmText}
      </button>
    </div>
  );

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleCancel}
      title={title}
      size="sm"
      variant={config.modalVariant}
      footer={footer}
      closeOnBackdrop={!loading}
      closeOnEscape={!loading}
      animation="zoom"
      ariaLabel={`${title} - Onay Dialog'u`}
      ariaDescribedBy="confirm-modal-message"
    >
      <div className="text-center py-4">
        {displayIcon && (
          <div
            className={`mx-auto mb-4 w-12 h-12 rounded-full bg-current/10 flex items-center justify-center ${config.iconClass}`}
          >
            {displayIcon}
          </div>
        )}

        <div id="confirm-modal-message" className={`leading-relaxed ${config.messageClass}`}>
          {message}
        </div>
      </div>
    </BaseModal>
  );
};

// =============================================================================
// Specialized Confirm Modal Variants
// =============================================================================

/**
 * Delete confirmation modal
 */
export const DeleteConfirmModal: React.FC<
  Omit<ConfirmModalProps, 'variant' | 'title' | 'confirmText'> & {
    itemName?: string;
    title?: string;
    confirmText?: string;
  }
> = ({ itemName, title = 'Silme Onayı', confirmText = 'Sil', message, ...props }) => {
  const defaultMessage = itemName
    ? `"${itemName}" öğesini silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`
    : 'Bu öğeyi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.';

  return (
    <ConfirmModal
      {...props}
      variant="danger"
      title={title}
      message={message || defaultMessage}
      confirmText={confirmText}
    />
  );
};

/**
 * Logout confirmation modal
 */
export const LogoutConfirmModal: React.FC<
  Omit<ConfirmModalProps, 'variant' | 'title' | 'message' | 'confirmText'>
> = props => {
  return (
    <ConfirmModal
      {...props}
      variant="warning"
      title="Çıkış Yap"
      message="Oturumunuzu kapatmak istediğinizden emin misiniz?"
      confirmText="Çıkış Yap"
    />
  );
};

/**
 * Save confirmation modal
 */
export const SaveConfirmModal: React.FC<
  Omit<ConfirmModalProps, 'variant' | 'title' | 'message' | 'confirmText'> & {
    hasUnsavedChanges?: boolean;
  }
> = ({ hasUnsavedChanges = true, ...props }) => {
  return (
    <ConfirmModal
      {...props}
      variant="info"
      title="Değişiklikleri Kaydet"
      message={
        hasUnsavedChanges
          ? 'Kaydedilmemiş değişiklikleriniz var. Kaydetmek istiyor musunuz?'
          : 'Değişiklikleri kaydetmek istediğinizden emin misiniz?'
      }
      confirmText="Kaydet"
    />
  );
};
